import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Link2, RefreshCw, ShieldCheck, Unplug, AlertCircle } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Card, PageTitle, Spinner, useToast } from "@/components/earnos/kit";

const button = "rounded-xl border border-line bg-surface-3 px-4 py-2 text-sm font-semibold text-ink hover:border-brand disabled:opacity-50";
const label = (p) => p.platform_status || "Unavailable";

export default function Connections() {
  const navigate = useNavigate();
  const toast = useToast();
  const [providers, setProviders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [service, setService] = useState("");
  const [goal, setGoal] = useState("");
  const [desiredAccess, setDesiredAccess] = useState("read");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [disconnectNotice, setDisconnectNotice] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [data, savedRequests] = await Promise.all([
        apiFetch("/connections"), apiFetch("/connections/requests"),
      ]);
      setProviders(Array.isArray(data) ? data : []);
      setRequests(Array.isArray(savedRequests) ? savedRequests : []);
      setError("");
    } catch (e) { setError(e.message || "Connections unavailable"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function start(provider) {
    if (busy) return;
    setBusy(provider.kind);
    try {
      const data = await apiFetch("/connections/" + encodeURIComponent(provider.kind) + "/start", {
        method: "POST", body: { private: false },
      });
      if (!data.authorize_url || !data.authorize_url.startsWith("https://")) {
        throw new Error("Secure sign-in is not available for this service.");
      }
      // OAuth credentials are entered on the provider's own website, never in EARNOS chat.
      window.location.assign(data.authorize_url);
    } catch (e) { toast.show(e.message, "error"); }
    finally { setBusy(""); }
  }

  async function requestConnection(event) {
    event.preventDefault();
    if (busy || !service.trim() || goal.trim().length < 8) return;
    setBusy("request");
    try {
      const result = await apiFetch("/connections/requests", {
        method: "POST",
        body: { service: service.trim(), goal: goal.trim(), desired_access: desiredAccess },
      });
      toast.show(result.capability?.state === "available_to_connect"
        ? "This provider has a supported connection path. See its card above."
        : "Connection request saved. No account access has been granted.", "success");
      setService("");
      setGoal("");
      await refresh();
    } catch (e) { toast.show(e.message || "Request failed", "error"); }
    finally { setBusy(""); }
  }

  async function disconnect(provider) {
    if (busy || !window.confirm("Disconnect " + provider.name + "? EARNOS will lose access to this account.")) return;
    setBusy(provider.kind);
    try {
      const result = await apiFetch("/connections/" + encodeURIComponent(provider.kind), { method: "DELETE" });
      if (!result.disconnected) throw new Error("Disconnection was not confirmed. Refresh and try again.");
      const message = provider.name + ": EARNOS access removed. " + (result.revoked
        ? "Provider access was revoked."
        : "Provider revocation could not be confirmed. Revoke EARNOS access in your provider account settings.") +
        (result.approval_cleanup_complete === false ? " Pending approval cleanup is incomplete; refresh your approvals before reconnecting." : "");
      setDisconnectNotice(message);
      toast.show("EARNOS access removed", "success");
      await refresh();
    } catch (e) { toast.show(e.message, "error"); }
    finally { setBusy(""); }
  }

  return (
    <div data-testid="earnos-connections" className="animate-fade-in space-y-5">
      <button onClick={() => navigate("/you")} className="inline-flex items-center gap-2 text-sm text-ink-2 hover:text-ink"><ArrowLeft size={17}/> Back to You</button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><PageTitle>Connected Apps</PageTitle>
          <p className="text-sm text-ink-2 mt-2 max-w-xl">Connect accounts you own. EARNOS only uses supported permissions and asks you to approve sensitive changes.</p>
        </div>
        <button className={button} onClick={refresh}><RefreshCw size={16} className="inline mr-1"/> Refresh</button>
      </div>
      {disconnectNotice ? <p role="status" className="text-sm text-ink-2">{disconnectNotice}</p> : null}
      {error ? <p role="alert" className="text-danger text-sm"><AlertCircle size={16} className="inline mr-1"/>{error}</p> : null}
      {loading ? <Spinner className="py-12" /> : providers.length === 0 ? <Card>No app connections are configured on this EARNOS server.</Card> :
        <div className="grid gap-3 md:grid-cols-2">
          {providers.map(p => (
            <Card key={p.kind}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <h2 className="font-bold text-lg text-ink">{p.name}</h2>
                  <p className="text-xs text-ink-2 mt-1">
                    {p.connected ? "Connected: " + (p.account || "your account") : label(p)}
                  </p>
                </div>
                <span className={"rounded-full px-2.5 py-1 text-xs font-bold border " + (p.connected ? "border-emerald-500/40 text-emerald-400" : "border-line text-ink-2")}>
                  {p.connected ? "Connected" : label(p)}
                </span>
              </div>
              {(p.read_actions || []).length ? <p className="text-sm text-ink-2 mt-3">Can read: {p.read_actions.join(", ")}</p> : null}
              {(p.write_actions || []).length ? <p className="text-sm text-ink-2 mt-2">Available actions: {p.write_actions.join(", ")}</p> : null}
              {(p.scopes || []).length ? <p className="text-xs text-ink-2 mt-2 break-all">Permissions: {p.scopes.join(", ")}</p> : null}
              {p.actions_note ? <p className="text-xs text-amber-300 mt-2">{p.actions_note}</p> : null}
              <div className="flex flex-wrap gap-2 mt-4">
                {p.connected ? <button disabled={Boolean(busy)} className={button} onClick={() => disconnect(p)}><Unplug size={15} className="inline mr-1"/>Disconnect</button> :
                  p.connection_required && p.oauth_configured ?
                    <button disabled={Boolean(busy)} onClick={() => start(p)} className={button}><Link2 size={15} className="inline mr-1"/>Sign in and connect <ExternalLink size={14} className="inline ml-1"/></button> :
                  <span className="text-xs text-ink-2 self-center">Connection not available yet. No credentials are requested here.</span>}
                {p.kind === "github" && p.connected ? <button className={button} onClick={() => navigate("/mission")}>Use in Mission Control</button> : null}
              </div>
            </Card>
          ))}
        </div>
      }
      <Card>
        <h2 className="text-lg font-bold text-ink">Connect another app</h2>
        <p className="text-sm text-ink-2 mt-2">Tell EARNOS which app you want and what it should do. We will show whether a verified connection exists. Requesting an app does not grant access.</p>
        <form onSubmit={requestConnection} className="mt-4 space-y-3">
          <label className="block text-sm text-ink-2" htmlFor="connection-service">App or service</label>
          <input id="connection-service" value={service} maxLength={80} required
            onChange={e => setService(e.target.value)} placeholder="Example: Canva or Notion"
            className="w-full rounded-xl bg-surface-3 border border-line px-3 py-2 text-ink" />
          <label className="block text-sm text-ink-2" htmlFor="connection-goal">What should EARNOS do there?</label>
          <textarea id="connection-goal" value={goal} maxLength={500} minLength={8} required rows={3}
            onChange={e => setGoal(e.target.value)} placeholder="Example: Create an editable proposal, let me approve it, then save it to my account"
            className="w-full rounded-xl bg-surface-3 border border-line px-3 py-2 text-ink" />
          <label className="block text-sm text-ink-2" htmlFor="connection-access">Access requested</label>
          <select id="connection-access" value={desiredAccess} onChange={e => setDesiredAccess(e.target.value)}
            className="w-full rounded-xl bg-surface-3 border border-line px-3 py-2 text-ink">
            <option value="read">Read information</option>
            <option value="draft">Create drafts for my approval</option>
            <option value="write">Approved changes in my account</option>
          </select>
          <p className="text-xs text-ink-2">Never enter passwords, login codes, API keys, or other secrets in this form. Sensitive actions always require specific approval.</p>
          <button type="submit" disabled={Boolean(busy) || goal.trim().length < 8 || !service.trim()} className={button}>Request this connection</button>
        </form>
        {requests.length > 0 ? <div className="mt-5 space-y-2">
          <h3 className="font-semibold text-ink">Your connection requests</h3>
          {requests.slice(0, 10).map((request) => <div key={request.request_id} className="border-t border-line pt-3">
            <p className="font-semibold text-sm text-ink">{request.service}</p>
            <p className="text-xs text-ink-2">{request.capability?.state === "available_to_connect" ? "Supported provider — use the sign-in option above" :
              request.capability?.state === "connected" || request.capability?.state === "connected_limited" ? "Connection exists — actions still require approval" :
              "Integration not yet available"}</p>
            <p className="text-xs text-ink-2 mt-1">{request.capability?.note}</p>
          </div>)}
        </div> : null}
      </Card>
      <Card className="!border-brand/30">
        <p className="flex gap-2 items-center text-ink font-semibold"><ShieldCheck size={18}/> Your accounts stay under your control</p>
        <p className="text-sm text-ink-2 mt-2">Signing in does not approve every action. Review each sensitive task in Mission Control. A service without a working integration cannot be represented as connected or usable.</p>
        <button className={button + " mt-3"} onClick={() => navigate("/browser-access")}>Manage allowed websites</button>
      </Card>
    </div>
  );
}

